import logger from '@/lib/logging';
import { getCorrelationId } from '@/lib/logging';
import {
  isInvalidCredentialFailure,
  requestSessionRecovery,
  SessionCredentialError,
  type SessionRecoveryOutcome,
} from '@/lib/session-recovery';
import { logRoutingMethod } from '@/lib/routing-debug';
import { getCurrentEnvironment, getAppHostingUrl } from '@/config/environment-config';
import { warnDirectAppHostingCall, isDirectAppHostingCall } from '@/lib/direct-call-detector';

/**
 * API Client Utility
 * 
 * Shared utility for making authenticated API requests to server endpoints.
 * All client-side database operations must use this or component-specific API clients.
 */

/**
 * Get the API base URL based on the current environment
 * 
 * IMPORTANT: Always returns empty string (relative URLs) for proper routing:
 * - Firebase Hosting (.web.app, .firebaseapp.com): Uses rewrites in firebase.json to route /api/* to App Hosting
 * - App Hosting (.hosted.app): Direct access, same origin, no rewrites needed
 * - Localhost: Served directly by Express (server.js)
 * 
 * Never use absolute URLs to App Hosting domain - always use relative URLs!
 */
function getApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return ''; // Server-side, use relative URLs
  }

  const hostname = window.location.hostname;
  const origin = window.location.origin;
  const isFirebaseHosting = hostname.endsWith('.web.app') || hostname.endsWith('.firebaseapp.com');
  const isAppHosting = hostname.includes('.hosted.app');
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  // Detect routing method
  // Firebase Hosting (.web.app, .firebaseapp.com) = static hosting that rewrites /api/* to App Hosting
  // App Hosting (.hosted.app) = Cloud Run backend, direct access
  const routingMethod = isFirebaseHosting ? 'rewrite' : isAppHosting ? 'direct' : isLocalhost ? 'local' : 'unknown';
  
  // Log routing method detection
  logRoutingMethod(
    {
      hostname,
      origin,
      isFirebaseHosting,
      isAppHosting,
      isLocalhost,
    },
    routingMethod
  );

  // Log routing information for Firebase Hosting
  if (isFirebaseHosting) {
    const env = getCurrentEnvironment();
    const appHostingUrl = getAppHostingUrl(env);
    
    logger.debug('🔄 [API] Using Firebase Hosting with rewrites to App Hosting', {
      category: 'api',
      hostname,
      appHostingUrl,
      routingMethod: 'rewrite',
      message: 'Firebase Hosting (.web.app, .firebaseapp.com) rewrites /api/* requests to App Hosting (.hosted.app) via firebase.json',
      note: 'Firebase Hosting = static hosting. App Hosting = Cloud Run backend.',
    });
  }

  // With Firebase Hosting rewrites configured in firebase.json,
  // we can use relative URLs for API calls on all environments:
  // - Localhost: served by Express (server.js) - same origin
  // - App Hosting (.hosted.app): served by Express (server.js) - same origin
  // - Firebase Hosting (.web.app, .firebaseapp.com): rewritten to App Hosting via firebase.json rewrites
  return '';
}

/** Retry on 503 (e.g. Vite SSR runner restarting in dev) with linear backoff. */
async function fetchWith503Retry(url: string, init: RequestInit): Promise<Response> {
  const maxRetries = 3;
  const baseDelayMs = 2000;
  let response = await fetch(url, init);
  for (let attempt = 0; attempt < maxRetries && response.status === 503; attempt++) {
    const delayMs = baseDelayMs * (attempt + 1);
    logger.debug(`[API] 503 from ${url}, retry ${attempt + 1}/${maxRetries} after ${delayMs}ms`, {
      category: 'api',
      url,
    });
    await new Promise((r) => setTimeout(r, delayMs));
    response = await fetch(url, init);
  }
  return response;
}

/** In-memory CSRF token cache — fetched once per page load, then reused. */
let _csrfToken: string | null = null;

/** Clear cached CSRF token (e.g. after logout). */
export function clearCsrfTokenCache(): void {
  _csrfToken = null;
}

/** Fetch (and cache) the double-submit CSRF token from the server. */
async function getCsrfToken(): Promise<string> {
  if (_csrfToken) return _csrfToken;
  const res = await fetch('/api/csrf-token', { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Failed to fetch CSRF token: ${res.status}`);
  }
  const data = await res.json();
  const token = data.token as string | undefined;
  if (!token) {
    throw new Error('CSRF token missing in response');
  }
  _csrfToken = token;
  return _csrfToken;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Mutating fetch with double-submit CSRF (cookie + x-csrf-token header).
 * Use for POST /api/auth/logout and other CSRF-gated auth mutations.
 */
export async function csrfMutatingFetch(
  path: string,
  options: RequestInit = {},
  _csrfRetried = false,
): Promise<Response> {
  const method = options.method ?? 'GET';
  const needsCsrf = !SAFE_METHODS.has(method.toUpperCase()) && typeof window !== 'undefined';

  let csrfHeaders: Record<string, string> = {};
  if (needsCsrf) {
    const token = await getCsrfToken();
    csrfHeaders = { 'x-csrf-token': token };
  }

  const response = await fetchWith503Retry(path, {
    ...options,
    credentials: options.credentials ?? 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-Id': getCorrelationId(),
      ...csrfHeaders,
      ...options.headers,
    },
  });

  if (
    needsCsrf &&
    !_csrfRetried &&
    response.status === 403
  ) {
    const errorText = await response.clone().text().catch(() => '');
    if (/invalid csrf token/i.test(errorText)) {
      logger.warn('[API] Invalid CSRF token — refreshing and retrying once', { path });
      clearCsrfTokenCache();
      return csrfMutatingFetch(path, options, true);
    }
  }

  return response;
}

/** Prefetch CSRF token after session is established (call before mutating /api/protected). */
export async function prefetchCsrfToken(): Promise<boolean> {
  try {
    await getCsrfToken();
    return true;
  } catch (error) {
    _csrfToken = null;
    logger.warn('[API] CSRF token prefetch failed', { error });
    return false;
  }
}

/**
 * Make an authenticated API request to a protected endpoint
 */
export async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit,
  /** @internal single CSRF refresh retry — do not pass from call sites */
  _csrfRetried = false,
  /** @internal single Session remint retry — do not pass from call sites */
  _sessionRetried = false,
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/api/protected${endpoint}`;
  const correlationId = getCorrelationId();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'unknown';

  // Detect and warn about direct app hosting calls
  const directCallDetected = isDirectAppHostingCall(url);
  if (directCallDetected) {
    warnDirectAppHostingCall(url, {
      file: 'src/lib/api-client.ts',
      function: 'apiFetch',
    });
  }

  const credentials = options?.credentials ?? 'include';
  const method = options?.method ?? 'GET';
  const needsCsrf = !SAFE_METHODS.has(method.toUpperCase()) && typeof window !== 'undefined';

  // Attach CSRF token for mutating requests to /api/protected (Finding #4)
  let csrfHeaders: Record<string, string> = {};
  if (needsCsrf) {
    try {
      const token = await getCsrfToken();
      csrfHeaders = { 'x-csrf-token': token };
    } catch (e) {
      logger.warn('[API] Could not fetch CSRF token; request may be rejected by server', { url });
    }
  }

  // Get current user's ID token if available (reserved for future Bearer use; session cookie is primary)
  let hasFirebaseUser = false;
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    hasFirebaseUser = Boolean(auth.currentUser);
  } catch {
    // Ignore if auth not initialized
  }

  logger.debug('📡 [API] Making API request', {
    category: 'api',
    correlationId,
    url,
    method,
    hostname,
    origin,
    isDirectAppHostingCall: directCallDetected,
    hasCredentials: credentials === 'include',
    hasFirebaseUser,
  });

  const defaultHeaders: Record<string, string> = {
    'X-Correlation-Id': correlationId,
    ...csrfHeaders,
  };
  if (!(options?.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetchWith503Retry(url, {
    ...options,
    credentials,
    headers: {
      ...defaultHeaders,
      ...options?.headers,
    },
  });

  // Dynamically update correlation ID from server response only if we don't already have a valid one
  const serverCorrelationId = response.headers.get('x-correlation-id');
  if (serverCorrelationId) {
    try {
      const { setCorrelationId, hasServerCorrelationId } = await import('@/lib/logging');
      if (!hasServerCorrelationId()) {
        setCorrelationId(serverCorrelationId);
      }
    } catch (e) {
      // Ignore if logging module not available
    }
  }

  // Check content type to detect HTML responses (which indicate routing issues)
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  // Detect if we're on Firebase Hosting (which can't serve API routes)
  const isFirebaseHosting = typeof window !== 'undefined' &&
    (window.location.hostname.endsWith('.web.app') ||
      window.location.hostname.endsWith('.firebaseapp.com'));

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let sessionOutcome: Exclude<SessionRecoveryOutcome, 'reminted'> | null = null;

    // If we got HTML instead of JSON, this is likely a routing issue
    if (!isJson && contentType.includes('text/html')) {
      const text = await response.text().catch(() => '');
      const preview = text.substring(0, 200);

      if (isFirebaseHosting) {
        // This shouldn't happen now that we're using Firebase Hosting rewrites, but keep the message helpful
        errorMessage = `API endpoint returned HTML instead of JSON. The API client uses relative URLs (expecting Firebase Hosting rewrites to App Hosting), but the request failed. ` +
          `Check firebase.json rewrites or try accessing via the App Hosting domain (.hosted.app) directly.`;
        logger.error(`[API] ${url} returned HTML on Firebase Hosting.`, {
          category: 'api',
          message: 'Firebase Hosting (.web.app, .firebaseapp.com) should rewrite /api/* to App Hosting (.hosted.app)',
        });
      } else {
        errorMessage = `API endpoint returned HTML instead of JSON. This usually means the route doesn't exist or there's a server error. Response preview: ${preview}`;
        logger.error(`[API] ${url} returned HTML:`, preview);
      }
    } else {
      // Try to parse as JSON for error details. Keep the HTTP status so callers
      // can detect 409 offboarded / 403 step-up without a custom Error subclass.
      try {
        const error = (await response.json()) as {
          error?: string;
          message?: string;
          reason?: { kind?: string };
        };
        const detail = [error.error, error.message, error.reason?.kind]
          .filter((part, index, parts) => Boolean(part) && parts.indexOf(part) === index)
          .join(': ');
        errorMessage = detail
          ? `HTTP ${response.status}: ${detail}`
          : `HTTP ${response.status}: ${response.statusText}`;

        // Expired or cleared Session while the SPA is still open. Remint once,
        // or let Auth end the Session, before callers render this as a data error.
        if (!_sessionRetried && isInvalidCredentialFailure(response.status, error)) {
          const outcome = await requestSessionRecovery();
          if (outcome === 'reminted') {
            return apiFetch<T>(endpoint, options, _csrfRetried, true);
          }
          sessionOutcome = outcome;
        }
      } catch (parseOrRecoveryError) {
        if (parseOrRecoveryError instanceof Error && parseOrRecoveryError.message.startsWith('HTTP ')) {
          throw parseOrRecoveryError;
        }
        // JSON parsing failed, or recovery threw — fall through to the status error.
      }
    }

    // Stale CSRF cookie/header after session mint — refresh once and retry
    if (
      needsCsrf &&
      !_csrfRetried &&
      response.status === 403 &&
      /invalid csrf token/i.test(errorMessage)
    ) {
      logger.warn('[API] Invalid CSRF token — refreshing and retrying once', { url });
      clearCsrfTokenCache();
      return apiFetch<T>(endpoint, options, true);
    }

    if (sessionOutcome) {
      throw new SessionCredentialError(errorMessage, sessionOutcome);
    }
    throw new Error(errorMessage);
  }

  // Ensure we got JSON before trying to parse
  if (!isJson) {
    const text = await response.text().catch(() => '');
    const preview = text.substring(0, 200);

    if (isFirebaseHosting && contentType.includes('text/html')) {
      const errorMessage = `API endpoint returned HTML on Firebase Hosting. Ensure rewrites are configured in firebase.json to route /api/* to App Hosting (.hosted.app) backend.`;
      logger.error(`[API] ${url} returned HTML on Firebase Hosting. Rewrites might be missing.`, {
        category: 'api',
        message: 'Firebase Hosting (.web.app, .firebaseapp.com) should rewrite /api/* to App Hosting (.hosted.app)',
      });
      throw new Error(errorMessage);
    }

    logger.error(`[API] ${url} returned non-JSON response (${contentType}):`, preview);
    throw new Error(`API endpoint returned ${contentType} instead of JSON. Response preview: ${preview}`);
  }

  return response.json();
}

/** Optional tuning for {@link pollEndpoint} change detection and callback payload. */
export type PollEndpointOptions<T> = {
  /**
   * When set, compared across polls instead of `JSON.stringify(data)`.
   * Use for list endpoints whose server order is non-deterministic (e.g. Firestore snapshot order).
   */
  getChangeKey?: (data: T) => string;
  /**
   * When set, the callback receives this transform of `data` (e.g. stable sort) instead of the raw response.
   */
  normalizeCallbackPayload?: (data: T) => T;
};

/**
 * Poll an endpoint at regular intervals for real-time updates
 * Returns cleanup function to stop polling
 */
export function pollEndpoint<T>(
  endpoint: string,
  callback: (data: T) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void,
  pollOptions?: PollEndpointOptions<T>
): () => void {
  let isActive = true;
  let fetchCount = 0;

  // Unique ID per subscription to track overlapping instances in logs
  const subscriptionId = Math.random().toString(36).slice(2, 8).toUpperCase();

  logger.debug(`🔔 [POLL] Subscription CREATED for ${endpoint}`, {
    category: 'poll',
    subscriptionId,
    intervalMs,
    // Capture approximate call stack location for debugging duplicate subscriptions
    caller: new Error().stack?.split('\n')[2]?.trim() ?? 'unknown',
  });

  let lastChangeKey = '';

  const fetchData = async () => {
    if (!isActive) return;
    fetchCount++;

    logger.debug(`⏱ [POLL] Fetching ${endpoint} (sub=${subscriptionId}, fetch #${fetchCount})`, {
      category: 'poll',
      subscriptionId,
      fetchCount,
    });

    try {
      const data = await apiFetch<T>(endpoint);

      const newChangeKey = pollOptions?.getChangeKey
        ? pollOptions.getChangeKey(data)
        : JSON.stringify(data);
      if (newChangeKey !== lastChangeKey) {
        lastChangeKey = newChangeKey;
        const payload = pollOptions?.normalizeCallbackPayload
          ? pollOptions.normalizeCallbackPayload(data)
          : data;
        callback(payload);
      } else {
        logger.debug(`🤐 [POLL] Data UNCHANGED for ${endpoint} (sub=${subscriptionId}), skipping callback`, {
          category: 'poll',
          subscriptionId,
        });
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      } else {
        logger.error(`Error polling ${endpoint}:`, error);
      }
    }
  };

  // Initial fetch
  fetchData();

  // Set up interval
  const intervalId = setInterval(fetchData, intervalMs);

  // Return cleanup function
  return () => {
    isActive = false;
    clearInterval(intervalId);
    logger.debug(`🔕 [POLL] Subscription DESTROYED for ${endpoint} (sub=${subscriptionId}, total fetches: ${fetchCount})`, {
      category: 'poll',
      subscriptionId,
      fetchCount,
    });
  };
}


